import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LIVON_LOGO = "https://static.prod-images.emergentagent.com/jobs/b293bd06-5db6-47e3-9bac-b35b794055ae/images/24e9f4cae61d982d9c454efb36258018ae47a7c7ef1d43817c3543666c110b4a.png";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Ange din e-postadress');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Kolla din inkorg!');
    } catch (error) {
      // Always show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/auth')}
          className="rounded-full"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Tillbaka
        </Button>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {!sent ? (
            <div className="bg-card rounded-3xl p-8 shadow-xl border border-border/50 animate-fade-in">
              {/* Logo */}
              <div className="text-center mb-8">
                <img 
                  src={LIVON_LOGO} 
                  alt="Livon" 
                  className="w-16 h-16 mx-auto mb-4"
                />
                <h1 className="font-heading text-2xl font-light">Glömt lösenord?</h1>
                <p className="text-muted-foreground mt-2">
                  Ange din e-postadress så skickar vi en återställningslänk.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">E-postadress</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="din@epost.se"
                      className="pl-10 rounded-xl bg-secondary/50 border-0 h-12"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl text-base font-medium"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Skicka återställningslänk
                </Button>
              </form>

              <p className="text-center text-sm text-muted-foreground mt-6">
                Kommer du ihåg lösenordet?{' '}
                <Link to="/auth" className="text-primary hover:underline">
                  Logga in
                </Link>
              </p>
            </div>
          ) : (
            <div className="bg-card rounded-3xl p-8 shadow-xl border border-border/50 animate-fade-in text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              
              <h1 className="font-heading text-2xl font-light mb-4">Kolla din inkorg!</h1>
              
              <p className="text-muted-foreground mb-6">
                Om det finns ett konto med adressen <strong>{email}</strong> har vi skickat 
                en länk för att återställa lösenordet.
              </p>
              
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Inget mail? Kolla skräpposten eller försök igen.
                </p>
                
                <Button
                  variant="outline"
                  onClick={() => setSent(false)}
                  className="rounded-full"
                >
                  Försök igen
                </Button>
              </div>
              
              <p className="text-center text-sm text-muted-foreground mt-6">
                <Link to="/auth" className="text-primary hover:underline">
                  Tillbaka till inloggning
                </Link>
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
